import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * Custom hook for auto-scrolling to elements based on URL query parameters
 * @param {Object} options Configuration options
 * @param {string} options.queryParam - Query parameter to watch (default: 'scroll')
 * @param {number} options.delay - Delay before scrolling in ms (default: 500)
 * @param {number} options.highlightDuration - Duration of highlight effect in ms (default: 2000)
 * @param {string} options.highlightColor - CSS color for highlight (default: 'rgba(0, 128, 255, 0.1)')
 * @param {Array} options.dependencies - Additional dependencies to trigger re-scroll (default: [])
 * @param {boolean} options.enableHighlight - Whether to apply highlight effect (default: true)
 */
export default function useAutoScroll(options = {}) {
  const {
    queryParam = "scroll",
    delay = 500,
    highlightDuration = 2000,
    highlightColor = "rgba(0, 128, 255, 0.1)",
    dependencies = [],
    enableHighlight = true,
  } = options;

  const location = useLocation();

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const scrollTarget = searchParams.get(queryParam);

    if (!scrollTarget) return;

    const timeoutId = setTimeout(() => {
      const element = document.getElementById(scrollTarget);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "center" });

        if (enableHighlight) {
          // Apply highlight effect
          const originalBackground = element.style.backgroundColor;
          element.style.transition = "background-color 0.3s ease";
          element.style.backgroundColor = highlightColor;

          setTimeout(() => {
            element.style.backgroundColor = originalBackground;
            setTimeout(() => {
              element.style.transition = "";
            }, 300);
          }, highlightDuration);
        }
      }
    }, delay);

    return () => clearTimeout(timeoutId);
  }, [location.search, queryParam, delay, highlightDuration, highlightColor, enableHighlight, ...dependencies]);
}